import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de l'Heure de la Prière Tahajjud - Calcul du Dernier Tiers de la Nuit";
const DESCRIPTION =
  "La calculatrice de l'heure de la prière Tahajjud est un outil qui vous aide à déterminer le meilleur moment pour accomplir la prière du Tahajjud, qui est le dernier tiers de la nuit. Entrez l'heure de la prière du Maghrib et l'heure de la prière du Fajr pour calculer le début et la fin de ce moment béni.";
const KEYWORDS =
  "calculatrice tahajjud, heure prière tahajjud, calcul dernier tiers nuit, comment calculer heure tahajjud, meilleur moment prière tahajjud, calculatrice prière de nuit";
const OG_URL = "https://do-calculate.com/calculator/fr/tahajjud-time/";

function TimeInput({
  label,
  hour,
  setHour,
  minute,
  setMinute,
  period,
  setPeriod,
  timeFormat,
}) {
  const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
  const hours24 = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  return (
    <div className="form-group mb-3">
      <b>
        <label className="form-label d-block mb-2">{label}</label>
      </b>
      <div className="d-flex align-items-center">
        <select
          value={hour}
          onChange={(e) => setHour(e.target.value)}
          className="form-select me-2"
          style={{ flex: 1 }}
        >
          {timeFormat === "12"
            ? hours12.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))
            : hours24.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
        </select>
        <span className="mx-1">:</span>
        <select
          value={minute}
          onChange={(e) => setMinute(e.target.value)}
          className="form-select mx-2"
          style={{ flex: 1 }}
        >
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        {timeFormat === "12" && (
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="form-select"
            style={{ flex: 1 }}
          >
            <option value="AM">Matin</option>
            <option value="PM">Soir</option>
          </select>
        )}
      </div>
    </div>
  );
}

function TahajjudCalculator() {
  const [timeFormat, setTimeFormat] = useState("12");
  const [maghribHour, setMaghribHour] = useState("6");
  const [maghribMinute, setMaghribMinute] = useState("30");
  const [maghribPeriod, setMaghribPeriod] = useState("PM");
  const [fajrHour, setFajrHour] = useState("4");
  const [fajrMinute, setFajrMinute] = useState("00");
  const [fajrPeriod, setFajrPeriod] = useState("AM");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateTahajjudTime = () => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    const get24HourFormat = (hourStr, minuteStr, period) => {
      let hour = parseInt(hourStr, 10);
      if (timeFormat === "12") {
        if (period === "PM" && hour !== 12) {
          hour += 12;
        }
        if (period === "AM" && hour === 12) {
          hour = 0;
        }
      }
      return `${hour.toString().padStart(2, "0")}:${minuteStr}`;
    };

    const maghribTime24 = get24HourFormat(maghribHour, maghribMinute, maghribPeriod);
    const fajrTime24 = get24HourFormat(fajrHour, fajrMinute, fajrPeriod);

    const maghrib = new Date(`2024-01-01T${maghribTime24}:00`);
    const fajr = new Date(`2024-01-02T${fajrTime24}:00`);

    if (fajr <= maghrib) {
      setError("L'heure du Fajr doit être après l'heure du Maghrib.");
      setIsLoading(false);
      return;
    }

    const nightDurationInMs = fajr - maghrib;
    const oneThirdOfNightInMs = nightDurationInMs / 3;
    const lastThirdStartTime = new Date(fajr.getTime() - oneThirdOfNightInMs);

    const formatTimeForDisplay = (date) => {
      return date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    setResult({
      start: formatTimeForDisplay(lastThirdStartTime),
      end: formatTimeForDisplay(fajr),
    });
    setIsLoading(false);
  };

  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px", lineHeight: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>Calculatrice de l'Heure du Tahajjud</h1>
          </div>
          <div className="border border-light p-3">
            <p>
              La calculatrice du dernier tiers de la nuit vous aide à connaître le meilleur moment
              pour accomplir la prière du Tahajjud, qui est l'un des moments les plus importants
              d'adoration et de supplication exaucée. L'outil calcule le temps entre l'appel à la
              prière du Maghrib et l'appel à la prière du Fajr pour déterminer précisément le début
              et la fin du dernier tiers de la nuit. Entrez simplement l'heure du Maghrib et du
              Fajr selon votre localité pour obtenir le résultat immédiat. Cette calculatrice est
              utile pour les musulmans souhaitant trouver facilement l'heure de la prière de nuit,
              surtout pendant le Ramadan ou les jours bénis.
            </p>
            <hr />
            <div className="row">
              <div className="col-sm-12 col-md-8 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white">
                  Entrez les heures de prière
                </div>
                <div className="d-flex justify-content-center mb-3">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${timeFormat === "12" ? "btn-success" : "btn-outline-success"}`}
                      onClick={() => setTimeFormat("12")}
                    >
                      Format 12 heures
                    </button>
                    <button
                      type="button"
                      className={`btn ${timeFormat === "24" ? "btn-success" : "btn-outline-success"}`}
                      onClick={() => setTimeFormat("24")}
                    >
                      Format 24 heures
                    </button>
                  </div>
                </div>
                <TimeInput
                  label="Heure de la prière du Maghrib"
                  hour={maghribHour}
                  setHour={setMaghribHour}
                  minute={maghribMinute}
                  setMinute={setMaghribMinute}
                  period={maghribPeriod}
                  setPeriod={setMaghribPeriod}
                  timeFormat={timeFormat}
                />
                <TimeInput
                  label="Heure de la prière du Fajr"
                  hour={fajrHour}
                  setHour={setFajrHour}
                  minute={fajrMinute}
                  setMinute={setFajrMinute}
                  period={fajrPeriod}
                  setPeriod={setFajrPeriod}
                  timeFormat={timeFormat}
                />
                <hr />
                <button
                  className="text-white bg-success w-100"
                  style={{ fontSize: "20px" }}
                  onClick={calculateTahajjudTime}
                  disabled={isLoading}
                >
                  {isLoading ? "Calcul en cours..." : "Calculer"}
                </button>
              </div>
              <div className="col-sm-12 col-md-6">
                <div className="p-3 mb-3 bg-success text-white">Résultat</div>
                {error && <div className="alert alert-danger"><b>Erreur :</b> {error}</div>}
                {result && (
                  <div className="alert alert-success text-center">
                    <h5>
                      Le dernier tiers de la nuit commence
                      <br />
                      (Meilleur moment pour le Tahajjud)
                    </h5>
                    <hr />
                    <p className="fs-5">
                      De : <b>{result.start}</b>
                    </p>
                    <p className="fs-5">
                      À : <b>{result.end}</b> (Appel du Fajr)
                    </p>
                  </div>
                )}
                {!result && !error && (
                  <div className="alert alert-info">
                    Les résultats s'afficheront ici après avoir saisi les heures et cliqué sur "Calculer".
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4 mx-auto">
          <MainCalculatorsList />
        </div>
      </div>
      <br />
      <div className="row">
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                Comment calculer l'heure de la prière du Tahajjud
              </div>
              <div className="info-section-body">
                <u>
                  <b>Calcul du dernier tiers de la nuit</b>
                </u>
                <br />
                <p>
                  La prière du Tahajjud est parmi les actes surérogatoires recommandés accomplis
                  la nuit. Le meilleur moment pour l'accomplir est le dernier tiers de la nuit,
                  où Allah, Glorifié et Exalté soit-Il, descend vers le ciel de ce monde.
                </p>
                <p>Pour calculer ce moment béni, la calculatrice suit les étapes suivantes :</p>
                <ol>
                  <li>
                    <b>Déterminer la durée de la nuit :</b> La durée totale en minutes est calculée
                    entre l'heure de l'appel à la prière du Maghrib et l'heure de l'appel à la
                    prière du Fajr.
                  </li>
                  <li>
                    <b>Diviser la nuit :</b> Cette durée est divisée en trois parties égales pour
                    déterminer la durée de chaque tiers de la nuit.
                  </li>
                  <li>
                    <b>Identifier le dernier tiers :</b> Le dernier tiers de la nuit commence en
                    soustrayant la durée d'un tiers de l'heure du Fajr, et se poursuit jusqu'à
                    l'appel du Fajr.
                  </li>
                </ol>
                <p>
                  <b>Exemple :</b> Si le Maghrib est à 18h00 et le Fajr à 4h30, la durée de la
                  nuit est de 10 heures et 30 minutes (630 minutes). La durée d'un tiers est de
                  3 heures et 30 minutes (210 minutes). En soustrayant cette durée de l'heure du
                  Fajr (4h30), on trouve que le dernier tiers commence à 1h00.
                </p>
                <br />
                <hr />
                <b>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</b>
              </div>
            </div>
          </div>
        </div>
      </div>
      <br />
    </div>
  );
}

export default TahajjudCalculator;
